import React, { useEffect, useState } from "react";
import { useEscape } from "@/hooks/useEscape";
import { Bell, BellOff, BellRing, CheckCircle2, Clock3, Inbox, X } from "lucide-react";
import { api } from "@/lib/api";
import { enablePushNotifications, getPushStatus, PUSH_SUPPORTED } from "@/lib/pushNotifications";
import { toast } from "sonner";

const EMPTY_SUMMARY = { total: 0, open: 0, in_progress: 0, addressed: 0 };

export default function PostLoginUpdate({ workspaceId }) {
  const [visible, setVisible] = useState(false);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [pushStatus, setPushStatus] = useState(PUSH_SUPPORTED ? "disabled" : "unsupported");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!workspaceId || sessionStorage.getItem("qedara_post_login_pending") !== "1") return;
    let active = true;
    setVisible(true);
    Promise.all([
      api.get("/feedback-summary/mine", { params: { workspace_id: workspaceId } }),
      getPushStatus().catch(() => "disabled"),
    ]).then(([summaryResponse, notificationStatus]) => {
      if (!active) return;
      setSummary(summaryResponse.data);
      setPushStatus(notificationStatus);
    }).catch((e) => {
      if (active) toast.error(e?.response?.data?.detail || "Noutățile despre tichetele tale nu s-au încărcat");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [workspaceId]);

  const close = () => {
    sessionStorage.removeItem("qedara_post_login_pending");
    setVisible(false);
  };

  useEscape(visible, close);

  const enableNotifications = async () => {
    setPushBusy(true);
    try {
      const next = await enablePushNotifications();
      setPushStatus(next);
      if (next === "enabled") toast.success("Notificări activate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Notificările nu au putut fi activate");
    } finally {
      setPushBusy(false);
    }
  };

  if (!visible) return null;

  const cards = [
    { label: "Abordate", value: summary.addressed, icon: CheckCircle2, color: "text-brand" },
    { label: "În lucru", value: summary.in_progress, icon: Clock3, color: "text-brand-2" },
    { label: "Deschise", value: summary.open, icon: Inbox, color: "text-fg/70" },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60" data-testid="post-login-update">
      <div className="w-full max-w-lg bg-surface border border-line/10 shadow-xl">
        <div className="px-5 py-4 border-b border-line/10 flex items-center justify-between">
          <div>
            <p className="label-tiny mb-1">// ACTUALIZARE PERSONALĂ</p>
            <h2 className="text-xl font-bold tracking-tight">Situația ticketelor tale</h2>
          </div>
          <button onClick={close} className="text-fg/50 hover:text-fg" aria-label="Închide">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <p className="text-sm text-fg/50">Se încarcă situația…</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {cards.map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="bg-surface-2 border border-line/10 p-3">
                    <Icon className={`w-4 h-4 mb-2 ${color}`} />
                    <div className="text-2xl font-bold">{value}</div>
                    <div className="text-[11px] text-fg/50">{label}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-fg/50">Total raportate de tine: {summary.total}</p>
            </>
          )}

          <div className="border-t border-line/10 pt-4">
            <div className="flex items-start gap-3">
              {pushStatus === "enabled"
                ? <BellRing className="w-5 h-5 text-brand mt-0.5" />
                : <Bell className="w-5 h-5 text-brand mt-0.5" />}
              <div className="flex-1">
                <h3 className="text-sm font-semibold">Notificări pentru actualizări</h3>
                <p className="text-xs text-fg/50 mt-1">
                  Primești notificări când ticketul este preluat, actualizat sau rezolvat.
                </p>
                {pushStatus === "disabled" && (
                  <button
                    onClick={enableNotifications}
                    disabled={pushBusy}
                    className="btn-primary text-xs px-3 py-1.5 mt-3 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="post-login-enable-notifications"
                  >
                    <Bell className="w-3.5 h-3.5" />
                    {pushBusy ? "Se activează…" : "Permite notificări"}
                  </button>
                )}
                {pushStatus === "enabled" && (
                  <p className="text-xs text-brand mt-2 flex items-center gap-1.5">
                    <BellRing className="w-3.5 h-3.5" /> Notificările sunt active
                  </p>
                )}
                {pushStatus === "denied" && (
                  <p className="text-xs text-destructive mt-2 flex items-center gap-1.5">
                    <BellOff className="w-3.5 h-3.5" /> Permisiunea este blocată în browser.
                  </p>
                )}
                {pushStatus === "unsupported" && (
                  <p className="text-xs text-fg/40 mt-2">Browserul nu suportă notificări push.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-line/10 flex justify-end">
          <button onClick={close} className="btn-primary text-sm px-4 py-1.5">Continuă</button>
        </div>
      </div>
    </div>
  );
}
